<template>
  <div>
    <HiraysHeader />

    <div class="main">
      <ul id="menu">
        <li data-menuanchor="page1" class="active"><a href="#page1"><span>{{ $t('homeMenu.page1') }}</span><i></i></a></li>
        <li data-menuanchor="page2"><a href="#page2"><span>{{ $t('homeMenu.page2') }}</span><i></i></a></li>
        <li data-menuanchor="page3"><a href="#page3"><span>{{ $t('homeMenu.page3') }}</span><i></i></a></li>
        <li data-menuanchor="page4"><a href="#page4"><span>{{ $t('homeMenu.page4') }}</span><i></i></a></li>
      </ul>

      <div id="fullpage">
        <!-- page1 banner -->
        <div class="section">
          <div class="banner1">
            <div class="banner1-content">
              <div class="swiper-container">
                <div class="swiper-wrapper">
                  <div v-for="(slide, idx) in localizedBanners" :key="'banner-' + idx" class="swiper-slide">
                    <div class="img">
                      <CompatibleVideo
                        v-if="slide.type === 'video'"
                        :src="$media(slide.video)"
                        :autoplay="true"
                        :muted="true"
                        :loop="true"
                        :controls="false"
                        :show-first-frame="false"
                        preload="auto"
                      />
                      <template v-else>
                        <img v-if="bannerImage(slide)" :src="bannerImage(slide)" @error="onBannerError($event)" alt="">
                        <div class="wrap">
                          <div class="textDiv move_b">
                            <div class="p1">{{ slide.title }}</div>
                            <div class="p2">{{ slide.subtitle }}</div>
                            <router-link
                              v-if="slide.link"
                              :to="slide.link"
                              class="flexcen imoreBtn"
                            >
                              <em>{{ bannerBtnLabel(slide) }}</em>
                            </router-link>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="swiper-button-prev banner1-nav flexcen" v-if="localizedBanners.length > 1">
                  <i class="font_family icon-zuojiantou"></i>
                </div>
                <div class="swiper-button-next banner1-nav flexcen" v-if="localizedBanners.length > 1">
                  <i class="font_family icon-youjiantou"></i>
                </div>
                <div class="swiper-pagination" v-if="localizedBanners.length > 1"></div>
              </div>
            </div>
            <img src="/template/cn/index/img/xia.png" alt="" class="downImg">
          </div>
        </div>

        <!-- page2 products — layout mirrors industry cases (full-bleed + bottom strip) -->
        <div
          class="section section1"
          :class="{ 'has-section-bg': !!productSectionBg }"
          :style="productSectionStyle"
        >
          <div class="banner2">
            <div class="indexDiv1">
              <div class="indexTitle cfff tc move_b">
                <span class="f54">{{ $t('home.productsTitle') }}</span>
              </div>

              <div class="product-hero">
                <div class="product-hero__visual">
                  <img
                    v-if="previewImage"
                    :key="previewImage"
                    :src="$media(previewImage)"
                    alt=""
                  >
                </div>
                <div class="product-hero__text textDiv flexbet" v-if="activeCategory">
                  <div class="wrap">
                    <span class="f36 bold">{{ activeCategory.name }}</span>
                    <div class="p product-hero__series" v-if="activeCategory.products && activeCategory.products.length">
                      <template v-for="(p, pIdx) in activeCategory.products">
                        <router-link
                          :key="p.id"
                          :to="'/product/' + activeCategory.slug + '/' + p.id + '.html'"
                          class="product-hero__series-item"
                          :class="{ on: activeProdIdx[activeCatIdx] === pIdx }"
                          @mouseenter.native="selectProduct(activeCatIdx, pIdx)"
                        >{{ p.shortName || p.name }}</router-link><span
                          v-if="pIdx < activeCategory.products.length - 1"
                          :key="'sep-' + p.id"
                          class="product-hero__series-sep"
                        >，</span>
                      </template>
                    </div>
                    <div class="product-hero__more" v-if="activeProductMoreLink">
                      <a
                        v-if="isExternalLink(activeProductMoreLink)"
                        :href="activeProductMoreLink"
                        class="flexcen imoreBtn"
                        target="_blank"
                        rel="noopener"
                      ><em>{{ $t('common.learnMore') }}</em></a>
                      <router-link
                        v-else
                        :to="activeProductMoreLink"
                        class="flexcen imoreBtn"
                      ><em>{{ $t('common.learnMore') }}</em></router-link>
                    </div>
                  </div>
                </div>
              </div>

              <div class="smallDiv move_t" v-if="localizedProductCategories.length">
                <div class="wrap1">
                  <div class="swiperDiv">
                    <div class="swiper-container gallery-thumbs" ref="productSwiperEl">
                      <div class="swiper-wrapper">
                        <div
                          v-for="(cat, idx) in localizedProductCategories"
                          :key="cat.slug"
                          class="swiper-slide"
                          :class="{ 'swiper-slide-thumb-active': idx === activeCatIdx }"
                          @mouseenter="selectCategory(idx)"
                          @click="selectCategory(idx)"
                        >
                          <div class="conDiv flexcen">
                            <p class="f20 bold">{{ cat.name }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="swiper-button-next flexcen"><i class="font_family icon-youjiantou"></i></div>
                    <div class="swiper-button-prev flexcen"><i class="font_family icon-zuojiantou"></i></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- page3 solutions -->
        <div class="section section2">
          <div class="banner2">
            <div class="indexDiv2">
              <div class="indexTitle cfff tc move_b">
                <span class="f54">{{ $t('home.solutionsTitle') }}</span>
                <p class="f28"></p>
              </div>
              <div class="swiper-container gallery-top">
                <div class="swiper-wrapper">
                  <div v-for="(sol, solIdx) in localizedSolutionSlides" :key="sol.slug + '-' + solIdx" class="swiper-slide">
                    <div class="imgDiv">
                      <img
                        v-if="solutionBannerImage(sol)"
                        :src="solutionBannerImage(sol)"
                        @error="onSolutionImgError"
                        alt=""
                      >
                    </div>
                    <div class="textDiv flexbet">
                      <div class="wrap">
                        <span class="f36 bold">{{ sol.label }}</span>
                        <div class="p" v-if="sol.desc"><p>{{ sol.desc }}</p></div>
                        <div class="solution-hero__more" v-if="solutionMoreLink(sol)">
                          <a
                            v-if="isExternalLink(solutionMoreLink(sol))"
                            :href="solutionMoreLink(sol)"
                            class="flexcen imoreBtn"
                            target="_blank"
                            rel="noopener"
                          ><em>{{ $t('common.learnMore') }}</em></a>
                          <router-link
                            v-else
                            :to="solutionMoreLink(sol)"
                            class="flexcen imoreBtn"
                          ><em>{{ $t('common.learnMore') }}</em></router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="smallDiv move_t">
                <div class="wrap1">
                  <div class="swiperDiv">
                    <div class="swiper-container gallery-thumbs">
                      <div class="swiper-wrapper">
                        <div v-for="(sol, solIdx) in localizedSolutionSlides" :key="'thumb-' + sol.slug + '-' + solIdx" class="swiper-slide">
                          <div class="conDiv flexcen">
                            <img
                              v-if="solutionThumb(sol)"
                              class="sol-thumb-icon"
                              :src="solutionThumb(sol)"
                              @error="onThumbError"
                              alt=""
                            >
                            <p class="f20 bold">{{ sol.label }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="swiper-button-next flexcen"><i class="font_family icon-youjiantou"></i></div>
                    <div class="swiper-button-prev flexcen"><i class="font_family icon-zuojiantou"></i></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- page4 about -->
        <div class="section section4">
          <div class="section4__bg" aria-hidden="true">
            <img
              v-if="aboutBgSrc"
              :key="aboutBgSrc"
              class="section4__bg-img"
              :src="aboutBgSrc"
              alt=""
              decoding="async"
              @load="onAboutBgLoad"
              @error="onAboutBgError"
            >
          </div>
          <div class="banner2">
            <div class="wrap">
              <div class="banner2-content indexDiv4">
                <div class="bdDiv">
                  <div class="indexTitle cfff tc move_b" v-if="homeAboutTitle || homeAboutSubtitle">
                    <span v-if="homeAboutTitle" class="f54">{{ homeAboutTitle }}</span>
                    <p v-if="homeAboutSubtitle" class="f28">{{ homeAboutSubtitle }}</p>
                  </div>
                  <div class="textDiv 18 move_b" v-if="homeAboutText">
                    <p style="text-align: center;">{{ homeAboutText }}</p>
                  </div>
                  <div class="dataDiv move_t" v-if="localizedStats.length">
                    <ul class="flexcen">
                      <li v-for="(stat, idx) in localizedStats" :key="idx">
                        <div class="conDiv">
                          <div class="num">
                            <span class="counter f52">{{ stat.display }}</span>
                            <sub v-if="stat.suffix" class="f30">{{ stat.suffix }}</sub>
                          </div>
                          <p>{{ stat.label }}</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <div class="flexcen moreBtn move_t">
                    <router-link to="/about/#page1" class="imoreBtn flexcen"><em>{{ $t('common.learnMore') }} </em></router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- footer section -->
        <div class="section fp-auto-height">
          <HiraysFooter />
        </div>
      </div>
    </div>

    <FloatingSidebar />
  </div>
</template>

<script>
import HiraysHeader from '@/components/hirays/Header.vue'
import HiraysFooter from '@/components/hirays/Footer.vue'
import FloatingSidebar from '@/components/hirays/FloatingSidebar.vue'
import CompatibleVideo from '@/components/hirays/CompatibleVideo.vue'
import { listIndexManager, listSiteStat, listProductCategory, listProduct, listSolutionCategory, listCompanyInfo } from '@/utils/http'
import { unwrapRows, mapSiteStats, resolveImageUrl, mapHomeProductCategories, mapHomeSolutionSlides, isVideoUrl, stripHtml } from '@/utils/content'
import { fetchSiteMedia, getProductSectionBg } from '@/utils/siteMedia'
import { destroySwiper, initIndexDiv1, initSolutionGallery } from '@/utils/swiperHelper'

const SOLUTION_NAME_SLUG = {
  Automotive: 'automotive', 'HVAC/R': 'hvac', Energy: 'energy', Batteries: 'batteries',
  Semiconductors: 'semiconductors', 'Consumer Electronics': 'consumer-electronics',
  'General Equipment': 'general-equipment', 'Medical Devices': 'medical',
  'Home Appliances': 'home-appliances', 'Aerospace & Defense': 'aerospace',
  'Advanced Materials': 'advanced-materials', 'Wires & Connectors': 'wires-connectors', Others: 'others'
}

const STAT_I18N_KEYS = ['statStandard', 'statPatents', 'statCases', 'statBase']

export default {
  name: 'Home',
  components: { HiraysHeader, HiraysFooter, FloatingSidebar, CompatibleVideo },
  data() {
    return {
      bannerSlides: [],
      productCategories: [],
      solutionSlides: [],
      stats: [],
      activeCatIdx: 0,
      activeProdIdx: { 0: 0 },
      productSwiper: null,
      solutionGallery: { thumbs: null, top: null },
      useFallbackStats: false,
      useFallbackProducts: false,
      useFallbackSolutions: false,
      aboutIntroText: '',
      aboutMainTitle: '',
      aboutSubtitleText: '',
      aboutPicUrl: '',
      aboutBgReady: false,
      productSectionBg: '',
    }
  },
  computed: {
    localizedBanners() {
      return this.bannerSlides
    },
    localizedProductCategories() {
      const list = this.productCategories.map(cat => ({
        ...cat,
        name: this.useFallbackProducts
          ? this.$navLabel('productNav', cat.slug, cat.name)
          : cat.name
      }))
      return list
    },
    localizedSolutionSlides() {
      return this.solutionSlides.map(sol => {
        const slug = sol.slug || SOLUTION_NAME_SLUG[sol.name] || sol.name
        return {
          ...sol,
          slug,
          label: sol.name || sol.label || '',
          desc: sol.desc || ''
        }
      })
    },
    localizedStats() {
      return this.stats.map((stat, i) => ({
        ...stat,
        label: (this.useFallbackStats && STAT_I18N_KEYS[i])
          ? this.$t('home.' + STAT_I18N_KEYS[i])
          : stat.label
      }))
    },
    homeAboutText() {
      if (this.aboutIntroText) return this.$cleanBrand(this.aboutIntroText)
      return ''
    },
    homeAboutTitle() {
      if (this.aboutMainTitle) return this.aboutMainTitle
      return ''
    },
    homeAboutSubtitle() {
      return this.aboutSubtitleText || ''
    },
    activeCategory() {
      return this.localizedProductCategories[this.activeCatIdx] || null
    },
    activeProduct() {
      const cat = this.activeCategory
      if (!cat || !cat.products || !cat.products.length) return null
      const pIdx = this.activeProdIdx[this.activeCatIdx] || 0
      return cat.products[pIdx] || cat.products[0]
    },
    previewTitle() {
      const p = this.activeProduct
      if (p) return p.shortName || p.name || ''
      const cat = this.activeCategory
      return cat ? cat.name : ''
    },
    previewImage() {
      const p = this.activeProduct
      if (p && p.image) return p.image
      const cat = this.activeCategory
      return cat ? cat.image : ''
    },
    activeProductMoreLink() {
      const p = this.activeProduct
      return p && p.linkUrl ? String(p.linkUrl).trim() : ''
    },
    aboutBgSrc() {
      if (!this.aboutPicUrl) return ''
      return this.$media(this.aboutPicUrl) || this.aboutPicUrl
    },
    productSectionStyle() {
      const style = { backgroundColor: '#819fdb' }
      if (this.productSectionBg) {
        const url = this.$media(this.productSectionBg)
        style.backgroundImage = `url(${url})`
        style.backgroundRepeat = 'no-repeat'
        style.backgroundPosition = 'center center'
        style.backgroundSize = 'cover'
      }
      return style
    }
  },
  watch: {
    localizedProductCategories: {
      handler(list) {
        if (!list || !list.length) return
        this.resetProductSelection(list)
        this.scheduleProductSwiperInit()
      }
    },
    localizedSolutionSlides: {
      handler(list) {
        if (!list || !list.length) return
        this.scheduleSolutionGalleryInit()
      }
    },
    aboutBgSrc(url) {
      if (!url) {
        this.aboutBgReady = false
        return
      }
      this.preloadAboutBg(url)
    }
  },
  created() {
    this.reloadCmsData()
  },
  mounted() {
    document.body.classList.add('page-home')
    this.resetProductSelection(this.localizedProductCategories)
    this.$nextTick(() => {
      setTimeout(() => {
        if (window.initHiraysHome) window.initHiraysHome()
        this.initProductSwiper()
        this.scheduleSolutionGalleryInit()
      }, 100)
    })
  },
  beforeDestroy() {
    document.body.classList.remove('page-home')
    destroySwiper(this.productSwiper)
    this.productSwiper = null
    destroySwiper(this.solutionGallery && this.solutionGallery.top)
    destroySwiper(this.solutionGallery && this.solutionGallery.thumbs)
    this.solutionGallery = { thumbs: null, top: null }
    if (window.destroyHiraysHome) window.destroyHiraysHome()
  },
  methods: {
    resetProductSelection(categories) {
      const prodIdx = {}
      ;(categories || []).forEach((cat, idx) => { prodIdx[idx] = 0 })
      this.activeProdIdx = prodIdx
      this.activeCatIdx = 0
    },

    selectCategory(idx, fromSwiper) {
      if (idx == null || idx < 0) return
      const changed = idx !== this.activeCatIdx
      this.activeCatIdx = idx
      if (changed) {
        this.$set(this.activeProdIdx, idx, 0)
      } else if (this.activeProdIdx[idx] == null) {
        this.$set(this.activeProdIdx, idx, 0)
      }
      if (!fromSwiper && this.productSwiper && this.productSwiper.activeIndex !== idx) {
        try { this.productSwiper.slideTo(idx) } catch (e) { /* noop */ }
      }
    },

    selectProduct(catIdx, prodIdx) {
      this.selectCategory(catIdx, true)
      this.$set(this.activeProdIdx, catIdx, prodIdx)
    },

    scheduleProductSwiperInit() {
      this.$nextTick(() => {
        window.setTimeout(() => this.initProductSwiper(), 80)
      })
    },

    scheduleSolutionGalleryInit() {
      this.$nextTick(() => {
        window.setTimeout(() => this.initSolutionGallery(), 120)
      })
    },

    initSolutionGallery() {
      destroySwiper(this.solutionGallery && this.solutionGallery.top)
      destroySwiper(this.solutionGallery && this.solutionGallery.thumbs)
      this.solutionGallery = { thumbs: null, top: null }
      const root = this.$el && this.$el.querySelector('.indexDiv2')
      if (!root || !this.localizedSolutionSlides.length) return
      // Wait one more frame so all Vue slides are in the DOM
      window.requestAnimationFrame(() => {
        const count = root.querySelectorAll('.gallery-top .swiper-slide').length
        if (count < this.localizedSolutionSlides.length) {
          window.setTimeout(() => {
            this.solutionGallery = initSolutionGallery(root)
          }, 80)
          return
        }
        this.solutionGallery = initSolutionGallery(root)
      })
    },

    initProductSwiper() {
      destroySwiper(this.productSwiper)
      this.productSwiper = null
      const root = this.$refs.productSwiperEl && this.$refs.productSwiperEl.closest('.indexDiv1')
      if (!root || !this.localizedProductCategories.length) return
      // Strip only scrolls via arrows — category changes via hover/click on cards
      this.productSwiper = initIndexDiv1(root, { slidesPerView: 6 })
      if (this.productSwiper && this.activeCatIdx > 0) {
        try { this.productSwiper.slideTo(this.activeCatIdx, 0) } catch (e) { /* noop */ }
      }
    },

    reloadCmsData() {
      this.loadHome()
    },
    async loadHome() {
      const locale = this.$i18n.locale

      const results = await Promise.allSettled([
        listIndexManager({ status: 1, pageNum: 1, pageSize: 20 }, locale),
        listSiteStat({ status: 1, pageNum: 1, pageSize: 10 }, locale),
        listProductCategory({ status: 1, pageNum: 1, pageSize: 50 }, locale),
        listProduct({ status: 1, pageNum: 1, pageSize: 500 }, locale),
        listSolutionCategory({ categoryType: 0, status: 1, pageNum: 1, pageSize: 100 }, locale),
        listCompanyInfo({ type: 0, status: 1, pageNum: 1, pageSize: 1 }, locale),
        fetchSiteMedia(true, locale)
      ])

      try {
        const media = results[6].status === 'fulfilled' ? results[6].value : null
        this.productSectionBg = getProductSectionBg(media)

        const aboutRes = results[5].status === 'fulfilled' ? results[5].value : null
        const aboutRow = aboutRes ? unwrapRows(aboutRes)[0] : null
        if (aboutRow) {
          if (aboutRow.title) {
            this.aboutMainTitle = this.$cleanBrand(aboutRow.title)
          } else {
            this.aboutMainTitle = ''
          }
          const content = String(aboutRow.content || '')
          const subtitleMatch = content.match(/<p[^>]*class="[^"]*subtitle[^"]*"[^>]*>([\s\S]*?)<\/p>/i)
          this.aboutSubtitleText = subtitleMatch ? stripHtml(subtitleMatch[1]) : ''
          const bodyHtml = subtitleMatch ? content.replace(subtitleMatch[0], '') : content
          this.aboutIntroText = stripHtml(bodyHtml)
          const aboutPics = aboutRow.picUrl ? aboutRow.picUrl.split(/[,，]/)[0].trim() : ''
          // Keep previous bg if CMS row has no pic (e.g. EN empty) — avoids blank flashes
          if (aboutPics) {
            const resolved = resolveImageUrl(aboutPics) || aboutPics
            this.aboutPicUrl = resolved
            this.preloadAboutBg(resolved)
          }
        } else if (aboutRes) {
          // Explicit empty CMS result — clear text, keep last known bg to avoid flicker
          this.aboutMainTitle = ''
          this.aboutSubtitleText = ''
          this.aboutIntroText = ''
        }
        // If request failed (aboutRes null), keep existing about fields

        const bannerRes = results[0].status === 'fulfilled' ? results[0].value : null
        if (bannerRes) {
          const banners = unwrapRows(bannerRes)
          const mapped = banners
            .map(b => {
              const url = b.indexPicurl || b.picUrl || ''
              const video = isVideoUrl(url)
              return {
                type: video ? 'video' : 'image',
                image: video ? '' : resolveImageUrl(url),
                video: video ? resolveImageUrl(url) : '',
                title: this.$cleanBrand(b.title || ''),
                subtitle: this.$cleanBrand(b.subtitle || ''),
                buttonText: this.$cleanBrand(b.buttonText || '').trim(),
                link: (b.linkUrl || '/').trim() || '/'
              }
            })
            .filter(b => b.image || b.video)
          if (mapped.length) {
            this.bannerSlides = mapped
            this.$nextTick(() => {
              window.setTimeout(() => {
                if (window.initHiraysHome) window.initHiraysHome()
              }, 50)
            })
          }
        }

        const statRes = results[1].status === 'fulfilled' ? results[1].value : null
        if (statRes) {
          const stats = mapSiteStats(unwrapRows(statRes))
          if (stats.length) {
            this.stats = stats
            this.useFallbackStats = false
          }
        }

        const catRes = results[2].status === 'fulfilled' ? results[2].value : null
        const prodRes = results[3].status === 'fulfilled' ? results[3].value : null
        if (catRes && prodRes) {
          const homeCats = mapHomeProductCategories(unwrapRows(catRes), unwrapRows(prodRes))
          this.productCategories = homeCats.length ? homeCats : []
          this.useFallbackProducts = false
        } else {
          this.productCategories = []
          this.useFallbackProducts = false
        }

        const solRes = results[4].status === 'fulfilled' ? results[4].value : null
        if (solRes) {
          const homeSols = mapHomeSolutionSlides(unwrapRows(solRes))
          this.solutionSlides = homeSols
          this.useFallbackSolutions = false
        } else {
          this.solutionSlides = []
        }
        this.scheduleSolutionGalleryInit()
      } catch (e) {
        console.error('Home load error', e)
      }
    },
    bannerImage(slide) {
      const url = slide.image || ''
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      return this.$media(url)
    },
    bannerBtnLabel(slide) {
      const text = String((slide && slide.buttonText) || '').trim()
      return text || this.$t('common.learnMore')
    },
    solutionBannerImage(sol) {
      const raw = sol.image || ''
      if (!raw) return ''
      if (/^https?:\/\//i.test(raw)) return raw
      const resolved = this.$media(raw)
      if (!resolved || resolved.includes('i-bg2.jpg') || resolved.includes('grey.gif')) return ''
      return resolved
    },
    onSolutionImgError(e) {
      e.target.style.display = 'none'
    },
    solutionThumb(sol) {
      const url = sol.thumb || ''
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      const resolved = this.$media(url)
      if (!resolved || resolved.includes('grey.gif')) return ''
      return resolved
    },
    solutionMoreLink(sol) {
      if (!sol || !sol.linkUrl) return ''
      return String(sol.linkUrl).trim()
    },
    isExternalLink(url) {
      return /^https?:\/\//i.test(String(url || '').trim())
    },
    preloadAboutBg(url) {
      const src = this.$media(url) || url
      if (!src) return
      const img = new Image()
      img.onload = () => { this.aboutBgReady = true }
      img.onerror = () => { /* keep solid fallback color */ }
      img.src = src
    },
    onAboutBgLoad() {
      this.aboutBgReady = true
    },
    onAboutBgError(e) {
      if (e && e.target) e.target.style.display = 'none'
      this.aboutBgReady = false
    },
    onThumbError(e) {
      e.target.style.visibility = 'hidden'
    },
    onBannerError(e) {
      // Do not fall back to legacy Hirays /images/banner-*.jpg assets
      e.target.removeAttribute('src')
      e.target.style.display = 'none'
    }
  }
}
</script>
